"use client";

/**
 * Providers - Root providers for the application
 *
 * Uses dynamic import to create a separate chunk for Privy SDK (~1MB).
 * SSR is ENABLED so children render as server HTML (critical for LCP).
 * The Privy chunk loads separately on the client for hydration.
 *
 * IMPORTANT: ssr:false was removed because it prevented ALL children from
 * being server-rendered, causing 30s LCP on slow 4G (browser had to download
 * 5.4MB of JS before painting ANY content).
 *
 * NOTE: an earlier revision tried to skip <ProvidersWithPrivy> on /embed/*
 * based on `usePathname()`. On SSR that hook can return a stale/null path,
 * so server and client disagreed on whether to mount Privy — React threw
 * hydration error #418, tore down the tree and Discord's RPC lost the
 * Activity's frame_id, breaking every pop-out button. Privy CSP errors
 * inside the iframe are cosmetic; keeping the provider mounted is cheaper
 * than losing the SDK handshake.
 */

import { ThemeProvider } from "next-themes";
import dynamic from "next/dynamic";
import { LocaleProvider } from "./components/LocaleProvider";
import type { Locale } from "./components/LanguageSelector";

// Dynamically import providers including Privy
// SSR enabled: server renders full HTML, client loads chunk for hydration
const ProvidersWithPrivy = dynamic(
  () => import("@/components/ProvidersWithPrivy").then(mod => mod.ProvidersWithPrivy),
);

export function Providers({
  children,
  clientId,
  initialLocale,
}: {
  children: React.ReactNode;
  clientId?: string;
  initialLocale: Locale;
}) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      storageKey="theme"
    >
      {/* LocaleProvider sits ABOVE Privy so every consumer of useT()
          inside or below the Privy tree sees the correct catalog from
          the first render. Seeded from the server-resolved cookie —
          eliminates the EN→ES flash that lived in useLocale.ts. */}
      <LocaleProvider initial={initialLocale}>
        <ProvidersWithPrivy clientId={clientId}>
          {children}
        </ProvidersWithPrivy>
      </LocaleProvider>
    </ThemeProvider>
  );
}
